import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { createCheckoutSession } from '../utils/products';
import { formatPrice } from '../utils/formatters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faTrash, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

const CheckoutPage: React.FC = () => {
  const { cart, removeFromCart, updateQuantity } = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity > 0) {
      updateQuantity(itemId, newQuantity);
    }
  };

  const removeFromCartHandler = (itemId: string) => {
    removeFromCart(itemId);
  };

  const handleCheckout = async () => {
    try {
      setIsLoading(true);
      setError(null);
      if (cart.items.length === 0) {
        setError('Your cart is empty. Please add items before checkout.');
        return;
      }

      for (const item of cart.items) {
        if (!item.id || !item.name || item.price <= 0 || item.quantity <= 0) {
          setError('Invalid item in cart. Please refresh and try again.');
          return;
        }
      }

      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/checkout/success`;
      const cancelUrl = `${baseUrl}/checkout/cancel`;

      const checkoutUrl = await createCheckoutSession(
        cart.items,
        successUrl,
        cancelUrl
      );

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        setError('Failed to create checkout session. Please try again.');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      const errorMessage = error.message || 'Failed to initiate checkout. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getImageSrc = (imagePath: string | undefined) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    try {
      return require(`../assets/images/${imagePath}`);
    } catch {
      return imagePath;
    }
  };

  return (
    <div className="pt-16 min-h-screen bg-gray-50 pb-16">
      <div className="container mx-auto px-4 py-12">
        <div className="flex items-center mb-8">
          <FontAwesomeIcon icon={faShoppingCart} className="mr-4 text-3xl text-[#2b603e]" />
          <h1 className="text-4xl font-bold text-gray-800">Your Cart</h1>
        </div>

        {cart.items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-md">
            <p className="text-2xl text-gray-600 mb-6">Your cart is empty</p>
            <Link
              to="/products"
              className="bg-[#2b603e] text-white px-8 py-3 rounded-lg hover:bg-[#1a3d25] transition-colors"
            >
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-6">
              {cart.items.map(item => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow-md p-6 flex items-center hover:shadow-xl transition-shadow"
                >
                  <div className="w-24 h-24 mr-6 flex-shrink-0">
                    <img
                      src={getImageSrc(item.images?.[0])}
                      alt={item.name}
                      className="w-full h-full object-cover rounded-md"
                    />
                  </div>

                  <div className="flex-grow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">{item.name}</h3>
                      </div>
                      <button
                        onClick={() => removeFromCartHandler(item.id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                        title="Remove from cart"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>

                    <div className="flex justify-between items-center mt-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center border border-gray-200 rounded-lg">
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded-l-lg"
                          >
                            <FontAwesomeIcon icon={faMinus} />
                          </button>
                          <span className="px-4 py-1 text-gray-800">{item.quantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded-r-lg"
                          >
                            <FontAwesomeIcon icon={faPlus} />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xl font-bold text-gray-800 mr-2">
                          {formatPrice(item.price * item.quantity)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b border-gray-200 pb-4">
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">{formatPrice(cart.totalPrice)}</span>
                </div>
                {cart.bulkDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Bulk Discount (17%)</span>
                    <span className="font-semibold">-{formatPrice(cart.bulkDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span>{formatPrice(cart.shippingCost)}</span>
                </div>
                <hr className="border-gray-200" />
                <div className="flex justify-between">
                  <span className="text-xl font-bold">Total</span>
                  <span className="text-xl font-bold text-[#2b603e]">{formatPrice(cart.totalWithShipping)}</span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                disabled={isLoading}
                className={`w-full py-4 rounded-lg text-white font-bold transition-all duration-300 flex items-center justify-center space-x-2 ${isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#2b603e] hover:bg-[#1a3d25] hover:shadow-lg'
                  }`}
              >
                {isLoading ? 'Processing...' : 'Proceed to Checkout'}
              </button>

              {error && (
                <div className="mt-4 text-center text-red-600 bg-red-50 py-2 rounded-lg">
                  {error}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage; 